import React from "react";
import { Badge, Button, Link, Spinner, Text } from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons/svg/arrow-clockwise";
import { ChevronDown20Regular } from "@fluentui/react-icons/svg/chevron-down";
import { ChevronUp20Regular } from "@fluentui/react-icons/svg/chevron-up";
import { ErrorCircle20Regular } from "@fluentui/react-icons/svg/error-circle";
import { OpenRegular } from "@fluentui/react-icons/svg/open";
import { useTranslation } from "react-i18next";
import type { ConnectionEditState } from "@onetasklist/shared";
import { useConnectionPreviewDockViewModel } from "./ConnectionPreviewDock.ViewModel";
import { useConnectionPreviewDockText } from "./ConnectionPreviewDock.i18n";
import "./ConnectionPreviewDock.scss";

interface ConnectionPreviewDockProps {
  boardId?: string;
  draftConnection: ConnectionEditState;
  /**
   * Lifts the dock clear of the form's sticky action footer, so it never covers Save.
   * Not needed when the page puts its actions in the header instead.
   */
  clearsStickyFooter?: boolean;
  /** Overrides the debounce; supplied by tests and Storybook. */
  debounceMs?: number;
}

/** Picks the one-line summary shown under the title, in priority order. */
const getStatusLine = (
  vm: ReturnType<typeof useConnectionPreviewDockViewModel>,
  text: ReturnType<typeof useConnectionPreviewDockText>,
): string => {
  if (vm.isLoading) return text.checking;
  if (vm.isPending) return text.waiting;
  if (vm.hasError) return vm.errorMessage;
  if (!vm.hasResult) return text.notRunYet;
  if (vm.isInactive) return text.inactive;
  if (vm.isEmpty) return text.noTasks;

  return vm.counts
    .map((count) => `${count.value} ${text[count.key]}`)
    .join(" · ");
};

export const ConnectionPreviewDock: React.FC<ConnectionPreviewDockProps> = ({
  boardId,
  draftConnection,
  clearsStickyFooter,
  debounceMs,
}) => {
  const { t } = useTranslation();
  const text = useConnectionPreviewDockText(t);
  const vm = useConnectionPreviewDockViewModel({
    boardId,
    draftConnection,
    loadErrorMessage: text.errorTitle,
    debounceMs,
  });

  if (!vm.isAvailable) {
    return null;
  }

  const statusLine = getStatusLine(vm, text);

  return (
    <aside
      className={`connection-preview-dock${
        vm.isExpanded ? " connection-preview-dock--expanded" : ""
      }${vm.isPending ? " connection-preview-dock--stale" : ""}${
        clearsStickyFooter ? " connection-preview-dock--above-footer" : ""
      }`}
    >
      <div className="connection-preview-dock__header">
        <button
          type="button"
          className="connection-preview-dock__summary"
          onClick={vm.toggleExpanded}
          aria-expanded={vm.isExpanded}
          aria-label={vm.isExpanded ? text.collapseAria : text.expandAria}
        >
          <span className="connection-preview-dock__summary-icon">
            {vm.isLoading ? (
              <Spinner size="tiny" aria-label={text.checking} />
            ) : vm.hasError ? (
              <ErrorCircle20Regular className="connection-preview-dock__error-icon" />
            ) : (
              <Text size={500} weight="semibold">
                {vm.hasResult ? vm.totalTasks : "—"}
              </Text>
            )}
          </span>
          <span className="connection-preview-dock__summary-text">
            <Text size={300} weight="semibold" block>
              {text.title}
            </Text>
            <Text size={200} block className="connection-preview-dock__status">
              {statusLine}
            </Text>
          </span>
          <span className="connection-preview-dock__chevron">
            {vm.isExpanded ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
          </span>
        </button>

        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowClockwise20Regular />}
          onClick={vm.refresh}
          disabled={vm.isLoading}
          aria-label={text.refresh}
          title={text.refresh}
        />
      </div>

      {vm.isExpanded && (
        <div className="connection-preview-dock__body">
          {vm.counts.length > 0 && (
            <div className="connection-preview-dock__counts">
              {vm.counts.map((count) => (
                <Badge key={count.key} appearance="tint" size="small">
                  {`${count.value} ${text[count.key]}`}
                </Badge>
              ))}
            </div>
          )}

          {vm.hasError && (
            <Text
              size={200}
              role="alert"
              block
              className="connection-preview-dock__error"
            >
              {vm.errorMessage}
            </Text>
          )}

          {!vm.hasError && vm.isInactive && (
            <Text size={200} block className="connection-preview-dock__empty">
              {text.inactive}
            </Text>
          )}

          {!vm.hasError && vm.isEmpty && (
            <Text size={200} block className="connection-preview-dock__empty">
              {text.noTasks}
            </Text>
          )}

          {!vm.hasError && vm.rows.length > 0 && (
            <ul className="connection-preview-dock__list">
              {vm.rows.map((row) => (
                <li key={row.key} className="connection-preview-dock__row">
                  <div className="connection-preview-dock__row-main">
                    {row.url ? (
                      <Link
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        className="connection-preview-dock__row-title"
                      >
                        {row.title}
                        <OpenRegular className="connection-preview-dock__row-open" />
                      </Link>
                    ) : (
                      <Text
                        size={200}
                        className="connection-preview-dock__row-title"
                      >
                        {row.title}
                      </Text>
                    )}
                    {row.subtitle && (
                      <Text
                        size={100}
                        block
                        className="connection-preview-dock__row-subtitle"
                      >
                        {row.subtitle}
                      </Text>
                    )}
                  </div>
                  <Badge appearance="outline" size="small" color={row.tone}>
                    {text.stateLabel(row.state)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}

          {vm.isTruncated && (
            <Text
              size={100}
              block
              className="connection-preview-dock__truncated"
            >
              {text.truncated}
            </Text>
          )}
        </div>
      )}
    </aside>
  );
};
